/**
 * MALWARE VERDICTS (PR 10e; the 10e contract; owner ruling H2 `guardduty`).
 *
 * GuardDuty Malware Protection for S3 scans every upload and keepr reads the
 * verdict. It gates only what strangers can reach (a public download); a
 * signed-in person is TOLD, never blocked. Every serialized attachment, and
 * the item read's `files` facts, carry:
 *
 *   scan: 'clean' | 'flagged' | 'checking' | 'unchecked'
 *
 * `checking` is a verdict on its way (seconds after an upload); `unchecked`
 * is a file the scanner never read (uploaded before the scan began, a type it
 * cannot read, or a read that failed). The web says nothing for `clean`, a
 * quiet "Not checked yet" for the two in between, and for `flagged` a plain
 * warning — with a confirm before anything fetches the original bytes
 * (Download, View full resolution: components/common/FileScanNote.js).
 *
 * A value this build does not know reads as no verdict at all: nothing is
 * said, and nothing is asked.
 */

export const SCAN_VALUES = ['clean', 'flagged', 'checking', 'unchecked'];

export const SCAN_FLAGGED_TEXT = 'Flagged by the malware scan. Download at your own risk.';
export const SCAN_UNCHECKED_TEXT = 'Not checked yet';

/** A file's verdict as the signed-in surfaces read it, or null. */
export const scanOf = (file) => {
	const value = file?.scan;
	return SCAN_VALUES.includes(value) ? value : null;
};

export const isFlaggedFile = file => scanOf(file) === 'flagged';

/** What a surface says about a file's verdict, or null for nothing. */
export function scanNoteFor(file) {
	const scan = scanOf(file);
	if (scan === 'flagged') return { tone: 'flagged', text: SCAN_FLAGGED_TEXT, short: 'Flagged' };
	if (scan === 'checking' || scan === 'unchecked') {
		return { tone: 'unchecked', text: SCAN_UNCHECKED_TEXT, short: SCAN_UNCHECKED_TEXT };
	}
	return null;
}

/**
 * ADMIN's reading (Admin → Attachments, the metrics page): the stored
 * verdict's six statuses — `clean` (NO_THREATS_FOUND), `flagged`
 * (THREATS_FOUND), `unsupported`, `failed` (FAILED or ACCESS_DENIED, re-read),
 * `pending` (no tag yet) and `unscanned` (uploaded before the scan began).
 * A row carrying the stored verdict (`scan: { status }`) is read as it is;
 * one carrying the signed-in four is read back to the nearest stored status
 * (`checking` is `pending`; `unchecked` stays itself, since it is any of
 * three). Anything else is null.
 */
export const SCAN_STATUSES = ['clean', 'flagged', 'unsupported', 'failed', 'pending', 'unscanned'];

export const SCAN_STATUS_LABELS = {
	clean: 'Clean',
	flagged: 'Flagged',
	unsupported: 'Unsupported',
	failed: 'Failed',
	pending: 'Checking',
	unscanned: 'Not scanned',
	unchecked: 'Not checked',
};

// antd's preset colours (no hex): red for the one that matters, gold for a
// verdict on its way (the house's in-flight colour), orange for a read that
// failed and will be retried, green for clean, plain for the rest.
export const SCAN_STATUS_COLORS = {
	clean: 'green',
	flagged: 'red',
	failed: 'orange',
	pending: 'gold',
};

export function scanStatusOf(row) {
	const raw = row?.scan;
	const stored = raw && typeof raw === 'object' ? raw.status : row?.scanStatus;
	if (SCAN_STATUSES.includes(stored)) return stored;
	if (raw === 'checking') return 'pending';
	if (raw === 'clean' || raw === 'flagged' || raw === 'unchecked') return raw;
	return null;
}
